import { OrderIngestType, SalesChannelType } from '@fd/customHooks/useQueryRMSSalesChannels';

import type {
  IntegratedStore,
  MarketplaceProperty,
  UberEatsStore,
} from './marketplaceIntegrations.services';

const MANAGE_PAGES: Partial<Record<SalesChannelType, { path: string; platformIcon: string }>> = {
  [SalesChannelType.UBER_EATS]: { path: 'uber-eats-integration-manage', platformIcon: 'UE' },
  [SalesChannelType.DELIVEROO]: { path: 'deliveroo-integration-manage', platformIcon: 'DE' },
};

// Must match the marketplace-integrations MFE's persisted zustand store (`store/manage/manage.ts`)
const MANAGE_STORE_KEY = 'manage-store';
const MFE_WINDOW_NAME = 'marketplace-integrations';

export const isMarketplaceManagedChannel = (
  salesChannelType: string | undefined,
  orderIngestType: string | undefined
) =>
  !!MANAGE_PAGES[salesChannelType as SalesChannelType] &&
  orderIngestType?.toLowerCase() === OrderIngestType.DIRECT_INTEGRATION.toLowerCase();

export const getMarketplaceListUrl = (appId: string, salesChannelType: string) =>
  `/${appId}/sales-channels/marketplaces/v2?type=${encodeURIComponent(salesChannelType)}`;

export const getMarketplaceManagePageUrl = (
  appId: string,
  salesChannelType: string,
  marketplaceStoreId: string
) => {
  const page = MANAGE_PAGES[salesChannelType as SalesChannelType];
  return page
    ? `/${appId}/sales-channels/marketplaces/v2/${page.path}/${encodeURIComponent(marketplaceStoreId)}`
    : undefined;
};

/**
 * The row the MFE's table builds for a connected store and hands to its Manage page.
 * Mirrors `useGetTableData` in the MFE's Uber Eats and Deliveroo lists.
 */
export const buildManageStoreDetails = ({
  store,
  properties,
  uberEatsStores = [],
}: {
  store: IntegratedStore;
  properties: MarketplaceProperty[];
  uberEatsStores?: UberEatsStore[];
}) => {
  const property = properties.find(({ propertyId }) => propertyId === store.propertyId);
  return {
    checked: store.marketplaceStoreId,
    id: store.marketplaceStoreId,
    platform: store.marketplace,
    platformIcon: MANAGE_PAGES[store.marketplace as SalesChannelType]?.platformIcon ?? '',
    marketplaceStoreName: store.marketplaceStoreName,
    property: property?.name,
    address: property?.address,
    menu: '',
    status: 'Connected',
    enabledAt: store.enabledAt,
    propertyId: store.propertyId,
    openingHours: store.openingHours,
    priceBandId: store.priceBandId,
    menuId: store.menuId,
    accessTokenExpired:
      uberEatsStores.find(
        ({ marketplaceStoreId }) => marketplaceStoreId === store.marketplaceStoreId
      )?.accessTokenExpired ?? null,
    brandId: store.brandId,
    salesChannelId: store.salesChannelId,
    holidayHoursUntil: store.holidayHoursUntil,
    storeUrl: store.storeUrl,
  };
};

export const writeManageStore = (storeDetails: ReturnType<typeof buildManageStoreDetails>) => {
  localStorage.setItem(MANAGE_STORE_KEY, JSON.stringify({ state: { storeDetails }, version: 0 }));
};

export const openWithFullPageLoad = (url: string) => window.location.assign(url);

/**
 * The MFE reads `manage-store` from localStorage only when its bundle first loads. Once loaded it
 * keeps the store in memory, so a full page load is the only way to make it pick up a new row.
 */
export const isMarketplaceMfeLoaded = () =>
  !!(window as unknown as Record<string, unknown>)[MFE_WINDOW_NAME] ||
  !!document.getElementById(`${MFE_WINDOW_NAME}-entry-script`);
